import React from 'react'
import { PowerPriceState, PowerPriceStateType } from 'src/Libs/dynamic-tariff-settings/dto/dynamic-tariff-settings.dto'
import { useFormContext } from 'react-hook-form'
import { TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import {
  DynamicTariffSettingsFormFields,
  SurchargeUnit,
  beginningOfDay,
  createEmptyWeekdaySelection,
  endOfDay,
} from 'src/Libs/dynamic-tariff-settings/constants'
import { resolveManualTariffOptionState } from 'src/Libs/dynamic-tariff-settings/utils'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'

interface Option {
  label: string
  value: PowerPriceStateType
  isDisabled?: boolean
  tooltip?: string
}

interface Props {
  disabled: boolean
  /* whether the persisted configuration already uses a manual tariff */
  hasExistingManualConfig: boolean
}

/**
 * Toggle button to manage power price state of dynamic tariff settings form
 */
const PowerPriceStateButtonGroup: React.FC<Props> = ({ disabled, hasExistingManualConfig }) => {
  const { t } = useCustomTranslation()
  const { setValue, clearErrors, getValues } = useFormContext()
  const {
    featureFlags: { isManualTariffEnabled },
  } = useFeatureFlagsContext()

  /**
   * The manual option is shown when the feature is enabled, or when it is disabled but the user
   * already has a manual tariff configured (so they can still switch away from it). In the latter
   * case the option itself is disabled, matching the "temporarily not available" behaviour.
   */
  const { showManualOption, isManualOptionDisabled } = resolveManualTariffOptionState(isManualTariffEnabled, hasExistingManualConfig)

  const options: Option[] = [
    ...(showManualOption
      ? [
          {
            label: t('dynamic-tariff-settings.power-price-state.manual'),
            value: PowerPriceState.Manual,
            isDisabled: isManualOptionDisabled,
            ...(isManualOptionDisabled ? { tooltip: t('dynamic-tariff-settings.manual-power-price.temporarily-unavailable') } : {}),
          },
        ]
      : []),
    { label: t('dynamic-tariff-settings.power-price-state.static'), value: PowerPriceState.Static },
    { label: t('dynamic-tariff-settings.power-price-state.dynamic'), value: PowerPriceState.Dynamic },
  ]

  const handlePowerPriceStateChange = (newState: PowerPriceStateType): void => {
    setValue(DynamicTariffSettingsFormFields.PowerPriceState, newState)
    clearErrors([
      DynamicTariffSettingsFormFields.PowerPrices,
      DynamicTariffSettingsFormFields.Taxes,
      DynamicTariffSettingsFormFields.SurchargePercent,
      DynamicTariffSettingsFormFields.SurchargeValue,
    ])

    /* Set default values for 'powerPrices' based on new 'powerPriceState' */
    switch (newState) {
      case PowerPriceState.Manual:
        setValue(
          DynamicTariffSettingsFormFields.PowerPrices,
          [
            {
              weekdays: createEmptyWeekdaySelection(),
              from: beginningOfDay,
              to: endOfDay,
              price: 0,
              description: '',
            },
          ],
          { shouldDirty: true }
        )
        break
      case PowerPriceState.Static:
        setValue(DynamicTariffSettingsFormFields.PowerPrices, 0, { shouldDirty: true })
        break
      default:
        /* 'PowerPriceState.Dynamic' — only seed defaults for fields that were never set, so switching back and forth keeps existing values */
        if (getValues(DynamicTariffSettingsFormFields.Taxes) === undefined) {
          setValue(DynamicTariffSettingsFormFields.Taxes, 0, { shouldDirty: true })
        }
        if (getValues(DynamicTariffSettingsFormFields.VatPercent) === undefined) {
          setValue(DynamicTariffSettingsFormFields.VatPercent, 0, { shouldDirty: true })
        }
        if (getValues(DynamicTariffSettingsFormFields.SurchargeUnit) === undefined) {
          setValue(DynamicTariffSettingsFormFields.SurchargeUnit, SurchargeUnit.Percent, { shouldDirty: true })
        }
        if (getValues(DynamicTariffSettingsFormFields.SurchargePercent) === undefined) {
          setValue(DynamicTariffSettingsFormFields.SurchargePercent, 0, { shouldDirty: true })
        }
        if (getValues(DynamicTariffSettingsFormFields.SurchargeValue) === undefined) {
          setValue(DynamicTariffSettingsFormFields.SurchargeValue, null, { shouldDirty: true })
        }
    }
  }

  const onChange = (value: string | number | boolean): void => {
    handlePowerPriceStateChange(value as PowerPriceStateType)
  }

  const suffixComponent = (
    <TabButtonGroup disabled={disabled} name={DynamicTariffSettingsFormFields.PowerPriceState} options={options} onChange={onChange} />
  )

  return (
    <SettingsBox
      title={t('dynamic-tariff-settings.power-price-state.title')}
      description={t('dynamic-tariff-settings.power-price-state.description-with-manual-option')}
      suffix={suffixComponent}
      disabled={disabled}
    />
  )
}

export default PowerPriceStateButtonGroup
